/**
 * Role helpers — the frontend mirror of app/authz.py (Rule 12 §3).
 *
 * Keycloak roles arrive on the NextAuth session as raw strings
 * (`session.roles`). Comparisons are case-insensitive and treat `-` and `_`
 * alike, so `AP-Manager`, `ap-manager` and `ap_manager` are the same role.
 *
 * Route access:
 *  - admin / AP-Manager: everything (AP-Manager's user list is scoped server-side).
 *  - user: everything except the admin section (unchanged legacy behaviour).
 *  - AP-Operator: Dashboard, Invoice Explorer and the read-only Workbench.
 *
 * The backend (app/authz.map.json) is the source of truth; these helpers only
 * decide what to render and where to redirect.
 */

export const ROLE_ADMIN = 'admin'
export const ROLE_USER = 'user'
export const ROLE_AP_MANAGER = 'ap-manager'
export const ROLE_AP_OPERATOR = 'ap-operator'

export function normalizeRole(role: string): string {
  return role.trim().toLowerCase().replace(/_/g, '-')
}

export function normalizeRoles(roles: readonly string[] | null | undefined): Set<string> {
  return new Set((roles ?? []).filter((r) => typeof r === 'string').map(normalizeRole))
}

export function hasRole(roles: readonly string[] | null | undefined, role: string): boolean {
  return normalizeRoles(roles).has(normalizeRole(role))
}

export const isAdmin = (roles?: readonly string[] | null) => hasRole(roles, ROLE_ADMIN)
export const isApManager = (roles?: readonly string[] | null) => hasRole(roles, ROLE_AP_MANAGER)
export const isApOperator = (roles?: readonly string[] | null) => hasRole(roles, ROLE_AP_OPERATOR)
export const isPlainUser = (roles?: readonly string[] | null) => hasRole(roles, ROLE_USER)

/** Admin section (User / Role / Group Management, Sessions, Events, Audit, Admin Settings). */
export const canManageAdmin = (roles?: readonly string[] | null) => isAdmin(roles) || isApManager(roles)

/** Read-only operator: Dashboard, Explorer, Workbench (view), Data Manager, Settings — no AI, no admin. */
export function isReadOnly(roles?: readonly string[] | null): boolean {
  // AP-Operator is a restriction: the generic `user` grant never widens it.
  return isApOperator(roles) && !isAdmin(roles) && !isApManager(roles)
}

/** Roles that clear the "pending approval" state. */
export function isApproved(roles?: readonly string[] | null): boolean {
  return isAdmin(roles) || isPlainUser(roles) || isApManager(roles) || isApOperator(roles)
}

/** Route allowlist for the read-only operator (prefix match on the first segment). */
export const OPERATOR_ROUTES = ['/', '/invoices', '/workbench', '/data-manager', '/settings'] as const

/** Routes that only admin / AP-Manager may open. */
export const ADMIN_ROUTE_PREFIX = '/admin'

function stripBasePath(pathname: string): string {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  if (basePath && pathname.startsWith(basePath)) return pathname.slice(basePath.length) || '/'
  return pathname
}

/**
 * Whether the given roles may open `pathname` (an app route, not an API path).
 * Auth routes are always allowed so the sign-in flow is never blocked.
 */
export function canAccess(pathname: string, roles?: readonly string[] | null): boolean {
  const path = stripBasePath(pathname)
  if (path.startsWith('/auth')) return true
  if (isAdmin(roles) || isApManager(roles)) return true
  if (path === ADMIN_ROUTE_PREFIX || path.startsWith(`${ADMIN_ROUTE_PREFIX}/`)) return false
  if (isReadOnly(roles)) {
    return OPERATOR_ROUTES.some((route) => (route === '/' ? path === '/' : path === route || path.startsWith(`${route}/`)))
  }
  // `user` (and any unrecognised role set) keeps the legacy behaviour: every
  // non-admin page; the backend still enforces the API side.
  return true
}
